import {
  CSSProperties,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  ActiveMove,
  ExtendedMove,
  GameShowProps,
  MovesHistory,
} from "@/types/types"
import { Piece } from "react-chessboard/dist/chessboard/types"
import { Chess, Color, Move, Square } from "chess.js"
import {
  appendLineToCurrentMove,
  appendMoveToCurrentLine,
  atFirstMoveOnCurrentLine,
  atLastMoveOnCurrentLine,
  findMatchingNextPosition,
  getCurrentLine,
  getCurrentMove,
  getCurrentMoveIndex,
  getNewLinePosition,
  getNextMovePosition,
  getParentMovePosition,
  getPreviousMovePosition,
  incrementMovePosition,
  removeCurrentLine,
  removeCurrentMove,
  replaceCurrentLine,
  updateAnnotationForCurrentMove,
  updateCommentForCurrentMove,
} from "@/utils/moveHelpers"
import { BB, BN, BQ, BR, WB, WN, WQ, WR } from "@/components/pieces"
import Modal from "@/components/Modal"
import { usePage } from "@inertiajs/react"
import usePuzzleAnalytics from "./usePuzzleAnalytics"

type GameMovesContext = {
  addAnnotation: (annotation: string) => void
  addComment: (comment: string) => void
  movesHistory: MovesHistory
  activeMove: ActiveMove
  game: Chess
  goToMove: (moveInfo: ActiveMove) => void
  addMove: (move: Move) => void
  addMoves: (moves: Move[]) => void
  discardChanges: () => void
  previousMove: () => void
  nextMove: () => void
  hasPreviousMove: boolean
  hasNextMove: boolean
  currentMove: ExtendedMove
  resetToMove: () => void
  undoMove: () => void
  onSquareClick: (square: Square) => void
  onPieceDragBegin: (square: Square) => void
  isDraggablePiece: (piece: Piece) => boolean
  onPieceDrop: (source: Square, target: Square) => boolean
  optionSquares: { [key: string]: CSSProperties }
  isShaking: boolean
}

const GameMovesContext = createContext<GameMovesContext>({} as GameMovesContext)

const promotionOptions = [
  {
    whiteIcon: WQ,
    blackIcon: BQ,
    text: "Promote to Queen",
    type: "q",
  },
  {
    whiteIcon: WR,
    blackIcon: BR,
    text: "Promote to Rook",
    type: "r",
  },
  {
    whiteIcon: WB,
    blackIcon: BB,
    text: "Promote to Bishop",
    type: "b",
  },
  {
    whiteIcon: WN,
    blackIcon: BN,
    text: "Promote to Knight",
    type: "n",
  },
]

export function GameMovesProvider({
  mode = "default",
  autoplaySide,
  children,
  initialGame,
  onMovesChange,
}: {
  mode?: "default" | "draft" | "replicate"
  autoplaySide?: Color
  children: ReactNode
  initialGame?: { start: string; moves: MovesHistory; chess960: boolean }
  onMovesChange?: (moves: MovesHistory) => void
}) {
  const { game: savedGame, puzzle } = usePage<GameShowProps>().props
  const gameSource = initialGame || savedGame || puzzle || {}
  const { moves: savedMoves = [], start: savedFen, chess960 = false } = gameSource

  const [game, setGame] = useState(new Chess(undefined, { chess960 }))
  const [activeMove, setActiveMove] = useState<ActiveMove>([])
  const [movesHistory, setMovesHistory] = useState<MovesHistory>(savedMoves)

  const updateMovesHistory = useCallback((newMoves: MovesHistory) => {
    setMovesHistory(newMoves)
    onMovesChange?.(newMoves)
  }, [onMovesChange])
  const [moveFrom, setMoveFrom] = useState<Square | null>(null)
  const [moveTo, setMoveTo] = useState<Square | null>(null)
  const [optionSquares, setOptionSquares] = useState({})
  const [promotionSquare, setPromotionSquare] = useState<Square | null>(null)
  const [promotionPiece, setPromotionPiece] = useState<string | null>(null)
  const [promotionFrom, setPromotionFrom] = useState<Square | null>(null)
  const [isShaking, setIsShaking] = useState(false)
  const { logPuzzleStarted, logPuzzleMistake } = usePuzzleAnalytics()

  useEffect(() => {
    let timeout: ReturnType<typeof setTimeout>

    if (isShaking) {
      timeout = setTimeout(() => setIsShaking(false), 300)
    }

    return () => clearTimeout(timeout)
  }, [isShaking])

  const currentMove = getCurrentMove(movesHistory, activeMove)

  const resetMoves = () => {
    setMoveFrom(null)
    setMoveTo(null)
    setOptionSquares({})
  }

  const discardChanges = () => {
    const chess = new Chess(undefined, { chess960 })
    if (savedFen) chess.load(savedFen)
    setGame(chess)
    updateMovesHistory(savedMoves)
    setActiveMove([])
  }

  // Sync movesHistory when the saved game data changes (e.g. after Inertia navigation).
  // Skip when initialGame is provided — those pages manage their own persistence.
  useEffect(() => {
    if (!initialGame) {
      setMovesHistory(savedMoves)
    }
  }, [savedMoves, initialGame])

  useEffect(() => {
    if (!savedFen) return

    try {
      const chess = new Chess(undefined, { chess960 })
      chess.load(savedFen)
      setGame(chess)
    } catch (error) {
      console.error("Error loading initial FEN", error)
    }
  }, [savedFen, chess960])

  useEffect(() => {
    const updateBoardPosition = () => {
      try {
        const chess = new Chess(undefined, { chess960 })
        if (savedFen) chess.load(savedFen)

        let lines = movesHistory

        activeMove.forEach((pos) => {
          const linePos = pos[0]
          const movePos = pos[1]

          const line = lines[linePos]
          line.slice(0, movePos + 1).map((move) => chess.move(move))

          lines = line[movePos].moves as MovesHistory
        })

        setGame(chess)
      } catch (error) {
        console.error("Error updating board position", error)
      }
    }

    updateBoardPosition()
  }, [activeMove, movesHistory, savedFen, chess960])

  const addAnnotation = (annotation: string) => {
    const newMoves = updateAnnotationForCurrentMove(
      movesHistory,
      activeMove,
      annotation
    )
    updateMovesHistory(newMoves)
  }

  const addComment = (comment: string) => {
    const newMoves = updateCommentForCurrentMove(
      movesHistory,
      activeMove,
      comment
    )
    updateMovesHistory(newMoves)
  }

  const simulateMove = useCallback(
    (move: Move) => {
      let moves = movesHistory

      if (mode === "replicate") {
        const matchingPosition = findMatchingNextPosition(
          moves,
          activeMove,
          move
        )

        if (activeMove.length === 0) {
          logPuzzleStarted()
        }

        if (!matchingPosition) {
          logPuzzleMistake()
          setIsShaking(true)

          return {
            moves,
            activeMove,
          }
        }
      }

      if (mode === "draft") {
        // For new games, reset to the current position, so we avoid creating variations
        const currentLine = getCurrentLine(moves, activeMove)
        const currentMoveIndex = getCurrentMoveIndex(activeMove)
        moves = replaceCurrentLine(
          moves,
          activeMove,
          currentLine.slice(0, Number(currentMoveIndex) + 1)
        )
      }

      // See if any of the next moves match
      const matchingPosition = findMatchingNextPosition(moves, activeMove, move)

      // Go to matching position
      if (matchingPosition) {
        return {
          moves,
          activeMove: matchingPosition,
        }
      }

      // Add the move to the current line if we're on the last move in the line
      else if (atLastMoveOnCurrentLine(moves, activeMove)) {
        return {
          moves: appendMoveToCurrentLine(moves, activeMove, move),
          activeMove: incrementMovePosition(activeMove),
        }
      }

      if (mode === "draft" && (activeMove[-1] || activeMove.length === 0)) {
        return {
          moves: appendLineToCurrentMove([], [], [move]),
          activeMove: getNewLinePosition([], []),
        }
      }

      // Otherwise add a new variation
      return {
        moves: appendLineToCurrentMove(moves, activeMove, [move]),
        activeMove: getNewLinePosition(moves, activeMove),
      }
    },
    [
      movesHistory,
      activeMove,
      mode,
      setIsShaking,
      logPuzzleMistake,
      logPuzzleStarted,
    ]
  )

  const simulateMoveReduce = (
    moves: MovesHistory,
    position: ActiveMove,
    move: Move
  ) => {
    // Add the move to the current line if we're on the last move in the line
    if (atLastMoveOnCurrentLine(moves, position)) {
      return {
        moves: appendMoveToCurrentLine(moves, position, move),
        activeMove: incrementMovePosition(position),
      }
    }

    // Otherwise add a new variation
    return {
      moves: appendLineToCurrentMove(moves, position, [move]),
      activeMove: getNewLinePosition(moves, position),
    }
  }

  const addMove = useCallback(
    (move: Move) => {
      const { moves, activeMove } = simulateMove(move)
      updateMovesHistory(moves)
      setActiveMove(activeMove)
    },
    [simulateMove, updateMovesHistory, setActiveMove]
  )

  useEffect(() => {
    if (!autoplaySide) return

    const nextMovePosition = getNextMovePosition(movesHistory, activeMove)
    if (!nextMovePosition) return

    const nextMove = getCurrentMove(movesHistory, nextMovePosition)
    if (nextMove.color !== autoplaySide) return

    setTimeout(() => addMove(nextMove), 500)
  }, [autoplaySide, activeMove, movesHistory, game, addMove])

  const addMoves = (moves: Move[]) => {
    const { moves: newMoves, activeMove: newActiveMoves } = moves.reduce(
      (result, move) =>
        simulateMoveReduce(result.moves, result.activeMove, move),
      { moves: movesHistory, activeMove }
    )

    updateMovesHistory(newMoves)
    setActiveMove(newActiveMoves)
  }

  const hasPreviousMove = activeMove?.length > 0
  const hasNextMove = !!getNextMovePosition(movesHistory, activeMove)

  const previousMove = () => {
    if (!hasPreviousMove) return
    const previousMove = getPreviousMovePosition(movesHistory, activeMove)
    setActiveMove(previousMove)
  }

  const nextMove = () => {
    const nextMove = getNextMovePosition(movesHistory, activeMove)
    if (!nextMove) return
    setActiveMove(nextMove)
  }

  const goToMove = (move: ActiveMove) => setActiveMove(move)

  const resetToMove = () => {
    const currentLine = getCurrentLine(movesHistory, activeMove)
    const currentMoveIndex = getCurrentMoveIndex(activeMove)

    const newMoves = replaceCurrentLine(
      movesHistory,
      activeMove,
      currentLine.slice(0, Number(currentMoveIndex) + 1)
    )

    updateMovesHistory(newMoves)
  }

  const undoMove = () => {
    // Remove the variation if we're at the first move in the line
    if (atFirstMoveOnCurrentLine(activeMove)) {
      updateMovesHistory(removeCurrentLine(movesHistory, activeMove))
      setActiveMove(getParentMovePosition(activeMove))
    } else {
      // Otherwise remove the last move from the line
      updateMovesHistory(removeCurrentMove(movesHistory, activeMove))
      if (atLastMoveOnCurrentLine(movesHistory, activeMove)) {
        setActiveMove(getPreviousMovePosition(movesHistory, activeMove))
      }
    }
  }

  // Get possible moves for clicked piece
  function getMoveOptions(square: Square) {
    const moves = game.moves({
      square,
      verbose: true,
    })

    if (moves.length === 0) {
      setOptionSquares({})
      return false
    }

    const newSquares: {
      [K in Square]?: CSSProperties
    } = {}

    moves.map((move) => {
      newSquares[move.to] = {
        background:
          game.get(move.to) &&
          game.get(move.to)?.color !== game.get(square)?.color
            ? "radial-gradient(circle, rgba(0,0,0,.2) 60%, transparent 60%)" // Style for capture
            : "radial-gradient(circle, rgba(0,0,0,.3) 25%, transparent 25%)", // Style for move
        borderRadius: "50%",
      }
      return move
    })

    setOptionSquares(newSquares)

    return true
  }

  // Check if we allow user to start dragging piece
  function isDraggablePiece(piece: Piece) {
    // Not allowed to drag if game is over
    if (game.isGameOver() || game.isDraw()) return false

    // Not allowed to drag if not user's turn
    if (!piece.startsWith(game.turn())) return false

    // Not allowed to drag if autoplay is on and it's the autoplayer's turn
    if (game.turn() === autoplaySide) return false

    return true
  }

  // Check if we allow user to drop piece
  function onPieceDrop(source: Square, target: Square) {
    const piece = game.get(source)

    if (
      piece?.type === "p" &&
      ((piece.color === "w" && target[1] === "8") ||
        (piece.color === "b" && target[1] === "1"))
    ) {
      setPromotionSquare(target)
      setPromotionFrom(source)
      return false
    }

    if (mode === "replicate") {
      const gameCopy = new Chess(game.fen(), { chess960 })

      try {
        const move = gameCopy.move({
          from: source,
          to: target,
          promotion: promotionPiece || "q",
        })

        const moves = movesHistory
        const matchingPosition = findMatchingNextPosition(
          moves,
          activeMove,
          move
        )

        if (activeMove.length === 0) {
          logPuzzleStarted()
        }

        if (!matchingPosition) {
          logPuzzleMistake()
          setIsShaking(true)
          return false
        }
      } catch (error) {
        resetMoves()
        return false
      }
    }

    let move

    try {
      move = game.move({
        from: source,
        to: target,
        promotion: promotionPiece || "q",
      })
    } catch {
      resetMoves()
      return false
    }

    addMove(move)
    resetMoves()
    return true
  }

  const setMoveOptions = (square: Square) => {
    const hasMoveOptions = getMoveOptions(square)
    if (hasMoveOptions) setMoveFrom(square)
  }

  function onPieceDragBegin(square: Square) {
    const currentMoveFrom = moveFrom

    resetMoves()

    if (!currentMoveFrom) return setMoveOptions(square)
  }

  const closePromotionModal = () => {
    setPromotionSquare(null)
    setPromotionPiece(null)
    setPromotionFrom(null)
  }

  const handlePromotion = (piece: string) => {
    if (!promotionSquare || !promotionFrom) return

    setPromotionPiece(piece)

    const move = game.move({
      from: promotionFrom,
      to: promotionSquare,
      promotion: piece,
    })

    addMove(move)
    resetMoves()
    closePromotionModal()
  }

  function onSquareClick(square: Square) {
    // Not allowed to click if autoplay is on and it's the autoplayer's turn
    if (game.turn() === autoplaySide) return false

    // Set the from square
    if (!moveFrom) return setMoveOptions(square)

    // From square is already set, user clicked the square to move to
    if (!moveTo) {
      // Check if move is valid
      const moves = game.moves({
        square: moveFrom,
        verbose: true,
      })
      const foundMove = moves.find(
        (m) => m.from === moveFrom && m.to === square
      )

      // Not a valid move
      if (!foundMove) {
        // Check if clicked on new piece
        const hasMoveOptions = getMoveOptions(square)
        // If new piece, setMoveFrom, otherwise clear moveFrom
        setMoveFrom(hasMoveOptions ? square : null)
        return
      }

      // Valid move
      const piece = game.get(moveFrom)
      if (
        piece?.type === "p" &&
        ((piece.color === "w" && square[1] === "8") ||
          (piece.color === "b" && square[1] === "1"))
      ) {
        setPromotionSquare(square)
        setPromotionFrom(moveFrom)
        return
      }

      setMoveTo(square)

      const currentFen = game.fen()
      const gameCopy = new Chess(currentFen, { chess960 })

      const move = gameCopy.move({
        from: moveFrom,
        to: square,
        promotion: promotionPiece || "q",
      })

      // If invalid, setMoveFrom and getMoveOptions
      if (move === null) {
        const hasMoveOptions = getMoveOptions(square)
        if (hasMoveOptions) setMoveFrom(square)
        return
      }

      addMove(move)
      resetMoves()
    }
  }

  return (
    <GameMovesContext.Provider
      value={{
        addAnnotation,
        addComment,
        movesHistory,
        addMove,
        activeMove,
        game,
        goToMove,
        addMoves,
        discardChanges,
        hasPreviousMove,
        hasNextMove,
        previousMove,
        nextMove,
        currentMove,
        resetToMove,
        undoMove,
        onSquareClick,
        onPieceDragBegin,
        isDraggablePiece,
        onPieceDrop,
        optionSquares,
        isShaking,
      }}
    >
      {children}
      <Modal
        show={!!promotionSquare}
        title="Promotion choice"
        onClose={closePromotionModal}
      >
        <div className="mt-4 grid grid-cols-2 gap-4">
          {promotionOptions.map(
            ({ whiteIcon: WIcon, blackIcon: BIcon, text, type }) => (
              <button
                key={type}
                onClick={() => handlePromotion(type)}
                className="focus-visible:outline-chessdrop-200 flex items-center justify-center rounded-2xl border border-gray-200 bg-white px-4 py-8 text-gray-900 shadow-xs hover:border-gray-400 focus-visible:outline-4"
              >
                {game.turn() === "w" ? (
                  <WIcon className="h-12 w-12" />
                ) : (
                  <BIcon className="h-12 w-12" />
                )}
                <span className="sr-only">{text}</span>
              </button>
            )
          )}
        </div>
      </Modal>
    </GameMovesContext.Provider>
  )
}

export function useGameMoves() {
  const context = useContext(GameMovesContext)
  if (context === undefined) {
    throw new Error("useGameMoves must be used within a GameMovesProvider")
  }
  return context
}
